import React, { useState, useMemo } from "react";
import { Form } from "react-bootstrap";
import { FaSearch, FaTimes } from "react-icons/fa";

/**
 * Reusable Field Selector Dropdown Component
 * Provides a searchable/filterable dropdown for selecting fields
 *
 * @param {Object} props
 * @param {Array} fields - Array of field objects
 * @param {string} value - Selected field name
 * @param {Function} onChange - Callback when field changes (fieldName)
 * @param {string} nameKey - Key for field name in field object (default: 'field_name')
 * @param {string} labelKey - Key for field label in field object (default: 'field_label')
 * @param {string} placeholder - Placeholder text (default: 'Select Field')
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {string} size - Bootstrap size ('sm', 'lg', etc.)
 * @param {Function} fieldFilter - Optional filter function for fields
 * @param {string} fieldTypeFilter - Optional field type filter (e.g., 'email', 'picklist')
 * @param {boolean} hideSubtitle - If true, do not show the second line (e.g. when nameKey is a UUID)
 * @param {string} subtitleKey - If set, use this property for the second line instead of nameKey
 */
function resolveFieldLabel(field, labelKey, nameKey) {
  const fromKey = (field[labelKey] || "").toString().trim();
  if (fromKey) return fromKey;
  const fallbacks = [
    field.field_display_name,
    field.field_label,
    field.label,
    field.field_name,
  ];
  for (const v of fallbacks) {
    const s = (v != null ? String(v) : "").trim();
    if (s) return s;
  }
  const idFallback = field[nameKey];
  return idFallback != null ? String(idFallback) : "";
}

export default function FieldSelectorDropdown({
  fields = [],
  value = "",
  onChange,
  nameKey = "field_name",
  labelKey = "field_label",
  placeholder = "Select Field",
  disabled = false,
  size = "",
  fieldFilter = null,
  fieldTypeFilter = null,
  hideSubtitle = false,
  subtitleKey = null,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Filter fields based on search term and field type
  const filteredFields = useMemo(() => {
    let filtered = [...fields];

    // Apply field type filter if provided
    if (fieldTypeFilter && fieldTypeFilter !== "all") {
      const types = fieldTypeFilter.split("|");
      filtered = filtered.filter((field) => {
        const fieldType = field.data_type?.toLowerCase() || "";
        return types.some((type) => fieldType.includes(type.toLowerCase()));
      });
    }

    // Apply custom filter if provided
    if (fieldFilter) {
      filtered = filtered.filter(fieldFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((field) => {
        const fieldName = (field[nameKey] || "").toLowerCase();
        const fieldLabel = (field[labelKey] || "").toLowerCase();
        return fieldName.includes(search) || fieldLabel.includes(search);
      });
    }

    return filtered;
  }, [fields, searchTerm, fieldTypeFilter, fieldFilter, nameKey, labelKey]);

  // Get selected field label
  const selectedField = fields.find(
    (f) => String(f[nameKey] ?? "") === String(value ?? "")
  );
  const selectedLabel = selectedField
    ? resolveFieldLabel(selectedField, labelKey, nameKey) || String(value ?? "")
    : "";

  // Handle field selection
  const handleSelect = (field) => {
    const fieldName = field[nameKey];
    onChange && onChange(fieldName != null ? String(fieldName) : "");
    setSearchTerm("");
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  // Handle input change (for search)
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setFocusedIndex(-1);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm("");
  };

  // Handle input blur (close dropdown after a delay to allow click)
  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      setFocusedIndex(-1);
    }, 200);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredFields.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredFields.length) {
          handleSelect(filteredFields[focusedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Clear search
  const handleClearSearch = (e) => {
    e.stopPropagation();
    setSearchTerm("");
    setFocusedIndex(-1);
  };

  return (
    <div className="position-relative">
      <div className="position-relative">
        {/* Input field with search icon */}
        <Form.Control
          type="text"
          value={isOpen ? searchTerm : selectedLabel}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          size={size}
          placeholder={placeholder}
          style={{
            paddingRight: "2.5rem",
            fontSize: "0.95rem",
            padding: "0.5rem 2.5rem 0.5rem 0.75rem",
            minHeight: "38px"
          }}
        />
        {/* Search/Clear icon */}
        <div
          className="position-absolute"
          style={{
            right: "0.5rem",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          {isOpen && searchTerm ? (
            <FaTimes
              className="text-muted"
              style={{ cursor: "pointer", pointerEvents: "auto" }}
              onClick={handleClearSearch}
            />
          ) : (
            <FaSearch className="text-muted" />
          )}
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div
          className="border rounded bg-white shadow-sm position-absolute w-100"
          style={{
            maxHeight: "300px",
            overflowY: "auto",
            zIndex: 1000,
            marginTop: "2px",
          }}
        >
          {filteredFields.length === 0 ? (
            <div className="p-2 text-muted text-center">
              {searchTerm ? "No fields found" : "No fields available"}
            </div>
          ) : (
            filteredFields.map((field, index) => {
              const fieldName = field[nameKey] != null ? String(field[nameKey]) : "";
              const fieldLabel = resolveFieldLabel(field, labelKey, nameKey);
              const subtitleRaw =
                subtitleKey != null && subtitleKey !== ""
                  ? field[subtitleKey]
                  : fieldName;
              const subtitle =
                subtitleRaw != null && String(subtitleRaw).trim() !== ""
                  ? String(subtitleRaw)
                  : "";
              const showSecondLine =
                !hideSubtitle && subtitle && subtitle !== fieldLabel;
              const isSelected = fieldName === String(value ?? "");
              const isFocused = index === focusedIndex;

              return (
                <div
                  key={fieldName || `field-${index}`}
                  className="p-2"
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      isFocused && !isSelected ? "#f8f9fa" : isSelected ? "#0d6efd" : "transparent",
                    color: isSelected ? "white" : "inherit",
                    padding: "0.625rem 0.75rem",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                    handleSelect(field);
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>{fieldLabel}</div>
                  {showSecondLine && (
                    <small className="text-muted" style={{ fontSize: "0.875rem", opacity: isSelected ? 0.8 : 1 }}>
                      {subtitle}
                    </small>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
